import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams } from "../http-params.util";

/**
 * AnalyticsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/analyticsApi/analyticsApi.service.js.
 *
 * Two sub-namespaces:
 *   apiService.analytics.revenue.{detail, summary}
 *   apiService.analytics.report.config.{datasources, getFields, updateFields}
 */
const ANALYTICS_REVENUE_BASE = "/v1/api/analytics/revenue";
const ANALYTICS_REPORT_CONFIG_BASE = "/v1/api/analytics/report/config";

const REVENUE_DETAIL_FIELDS = [
  "sortOrder",
  "startTime",
  "endTime",
  "limit",
] as const;

@Injectable({ providedIn: "root" })
export class AnalyticsApiService {
  private readonly http = inject(HttpClient);

  readonly revenue = {
    detail: (props: Record<string, unknown>): Promise<AjsResponse<unknown>> => {
      const params = buildParams({ limit: 5, ...props }, REVENUE_DETAIL_FIELDS);
      return wrapAjs(
        this.http.get<unknown>(ANALYTICS_REVENUE_BASE, { params }),
      );
    },

    summary: (date: string): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${ANALYTICS_REVENUE_BASE}/summary`, {
          params: buildParams({ date }),
        }),
      ),
  };

  readonly report = {
    config: {
      datasources: (): Promise<AjsResponse<unknown>> =>
        wrapAjs(
          this.http.get<unknown>(
            `${ANALYTICS_REPORT_CONFIG_BASE}/datasourcenames`,
          ),
        ),

      getFields: (datasource: string): Promise<AjsResponse<unknown>> =>
        wrapAjs(
          this.http.get<unknown>(
            `${ANALYTICS_REPORT_CONFIG_BASE}/fields/${datasource}`,
          ),
        ),

      updateFields: (props: {
        dataSourceName: string;
        fieldNames: string[];
      }): Promise<AjsResponse<unknown>> =>
        wrapAjs(
          this.http.post<unknown>(`${ANALYTICS_REPORT_CONFIG_BASE}/fields`, {
            dataSourceName: props.dataSourceName,
            fieldNames: props.fieldNames,
          }),
        ),
    },
  };
}
